import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShoppingBagIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';

import { useStateContext } from '../../../contexts/ContextProvider';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useHousekeepingQuickReport } from '../../../hooks/useHousekeepingQuickReport';
import { estAFaire } from '../../../domain/menage/aFaire';
import { useLeverNePasDeranger } from '../../api/useActionsMenage';
import { useChecklistsDeLaChambre, useMesChambres } from '../../api/useMesChambres';
import Button from '../../components/Button';
import EtatErreur from '../../components/EtatErreur';
import Screen from '../../components/Screen';
import Text from '../../components/Text';
import Tuile from '../../components/Tuile';
import { emplacementChambre, libelleStatut, libelleType, typeIndetermine } from '../../helpers/menage';
import { echecSansDonnee } from '../../helpers/requete';
import { t } from '../../i18n/langue';

import BlocChecklist from './BlocChecklist';
import BlocNotesChambre from './BlocNotesChambre';
import BlocPreparations from './BlocPreparations';
import ChampTexte from './ChampTexte';
import Chrono from './Chrono';
import EnteteFiche from './EnteteFiche';
import FeuilleDemarrer from './FeuilleDemarrer';
import FeuilleNePasDeranger from './FeuilleNePasDeranger';
import FeuilleSignalement from './FeuilleSignalement';
import { useFinDuMenage } from './useFinDuMenage';

/**
 * La fiche ménage d'une chambre — lot 3 Odomo.
 *
 * La chambre est lue dans « Mes chambres » (même cache que la liste), pas dans le
 * tableau de la gouvernante : une chambre retirée à l'agent disparaît donc de sa
 * fiche, avec une phrase qui le dit. Hors périmètre mobile (plan) : photo du « ne
 * pas déranger », « terminer de force », historique.
 *
 * Une seule feuille à la fois (#34) : démarrer, « ne pas déranger », signalement
 * (problème ou objet trouvé, parcours « photo d'abord » partagé avec le web).
 */
type Feuille = 'demarrer' | 'ndd' | null;

export default function FicheMenage() {
    const navigate = useNavigate();
    const { serviceId } = useParams<{ serviceId: string }>();
    const { currentFacility } = useStateContext();
    const { hasPermission } = usePermissions();
    const peutAffecter = hasPermission('housekeeping.assign');

    const { data, isLoading, isError, refetch } = useMesChambres({ facilityId: currentFacility?.id ?? null });
    const chambre = data?.find((c) => c.service_id === Number(serviceId));
    const enCours = chambre?.status === 'cleaning';
    const checklists = useChecklistsDeLaChambre(enCours ? chambre?.applicable_checklist_template_ids ?? [] : []);
    const fin = useFinDuMenage(chambre, checklists.templates);
    const lever = useLeverNePasDeranger();
    const signalement = useHousekeepingQuickReport();

    const [feuille, setFeuille] = useState<Feuille>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [alerteNdd, setAlerteNdd] = useState(false);
    const retour = () => navigate('/housekeeping');
    const apres = (texte: string) => () => {
        setFeuille(null);
        setMessage(texte);
    };

    if (!chambre) {
        const echec = echecSansDonnee({ isError, data });
        return (
            <Screen entete={<EnteteFiche titre={t('menage.titre')} onRetour={retour} />}>
                <div className="flex flex-col gap-3 px-4 py-4">
                    {isLoading && (
                        <Text variante="petit" ton="attenue">
                            {t('commun.chargement')}
                        </Text>
                    )}
                    {echec && <EtatErreur onReessayer={() => void refetch()} />}
                    {!isLoading && !echec && (
                        <>
                            <Text>{t('menage.fiche.plusAffectee')}</Text>
                            <Button libelle={t('menage.retourMesChambres')} variante="secondaire" onPress={retour} />
                        </>
                    )}
                </div>
            </Screen>
        );
    }

    const peutCommencer = !typeIndetermine(chambre) || peutAffecter;
    const sousTitre = [emplacementChambre(chambre), libelleType(chambre.cleaning_type), libelleStatut(chambre.status)]
        .filter(Boolean)
        .join(' · ');

    const leverNdd = () => {
        setAlerteNdd(false);
        lever.mutate(
            { serviceId: chambre.service_id },
            { onSuccess: apres(t('menage.fiche.nddLeve')), onError: () => setAlerteNdd(true) },
        );
    };

    const pied = (estAFaire(chambre) || enCours) && (
        <div className="flex flex-col gap-2 border-t border-border-color-light bg-white px-4 py-3">
            {fin.alerte && (
                <div role="alert">
                    <Text variante="petit" ton="danger">
                        {fin.alerte}
                    </Text>
                </div>
            )}
            {enCours ? (
                <Button
                    libelle={t('menage.fiche.terminer')}
                    pleineLargeur
                    // Jamais bloqué par une checklist INDISPONIBLE : le web laisse terminer et le
                    // serveur ne refuse pas. Seul le chargement en cours fait attendre.
                    disabled={fin.enCours || checklists.isLoading}
                    onPress={fin.terminer}
                />
            ) : (
                <Button
                    libelle={t('menage.fiche.commencer')}
                    pleineLargeur
                    disabled={!peutCommencer}
                    onPress={() => setFeuille('demarrer')}
                />
            )}
        </div>
    );

    return (
        <Screen entete={<EnteteFiche titre={chambre.room_name} sousTitre={sousTitre} onRetour={retour} />} pied={pied}>
            <div className="flex flex-col gap-5 px-4 py-4">
                {message && (
                    <div role="status" className="rounded-xl bg-main-color-light px-3 py-2">
                        <Text variante="petit" ton="marque">
                            {message}
                        </Text>
                    </div>
                )}

                {enCours && chambre.cleaning_started_at && <Chrono debut={chambre.cleaning_started_at} />}

                {!peutCommencer && estAFaire(chambre) && (
                    <Text variante="petit" ton="attenue">
                        {t('menage.fiche.typeIndetermine')}
                    </Text>
                )}

                <BlocNotesChambre serviceId={chambre.service_id} />
                <BlocPreparations preparations={chambre.preparations ?? []} />

                {enCours && checklists.isLoading && (
                    <Text variante="petit" ton="attenue">
                        {t('menage.fiche.chargementChecklist')}
                    </Text>
                )}
                {enCours && checklists.isError && (
                    // Non bloquant : sans checklist, « Terminer » envoie la fin simple,
                    // comme le web. Les points obligatoires ne bloquent que chargés.
                    <div role="alert" className="flex flex-col items-start gap-2">
                        <Text variante="petit" ton="danger">
                            {t('menage.fiche.checklistIndisponible')}
                        </Text>
                        <Button libelle={t('commun.reessayer')} variante="secondaire" onPress={checklists.recharger} />
                    </div>
                )}
                {enCours && checklists.templates.length > 0 && (
                    <>
                        <BlocChecklist
                            templates={checklists.templates}
                            coches={fin.coches}
                            onBasculer={fin.basculer}
                            manquant={fin.manquant}
                        />
                        <ChampTexte libelle={t('menage.fiche.notes')} valeur={fin.notes} onChange={fin.setNotes} />
                    </>
                )}

                <div className="flex flex-col gap-2">
                    {alerteNdd && (
                        <div role="alert">
                            <Text variante="petit" ton="danger">
                                {t('menage.fiche.nddLeveEchec')}
                            </Text>
                        </div>
                    )}
                    {chambre.is_dnd ? (
                        <Button
                            libelle={t('menage.fiche.leverNdd')}
                            variante="secondaire"
                            disabled={lever.isLoading}
                            onPress={leverNdd}
                        />
                    ) : (
                        <Button libelle={t('menage.fiche.nePasDeranger')} variante="secondaire" onPress={() => setFeuille('ndd')} />
                    )}
                </div>

                {/* Signalement photo d'abord : l'input vit ici, l'appareil photo ne
                    s'ouvre que dans le geste (voir useHousekeepingQuickReport). */}
                <div className="grid grid-cols-2 gap-3">
                    <Tuile
                        libelle={t('menage.fiche.probleme')}
                        icone={WrenchScrewdriverIcon}
                        teinte="ambre"
                        disabled={feuille !== null}
                        onPress={() => signalement.start('issue', chambre.service_id)}
                    />
                    <Tuile
                        libelle={t('menage.fiche.objetTrouve')}
                        icone={ShoppingBagIcon}
                        teinte="ciel"
                        disabled={feuille !== null}
                        onPress={() => signalement.start('lost', chambre.service_id)}
                    />
                </div>
                <input {...signalement.inputProps} data-testid="photo-signalement" />
            </div>

            {feuille === 'demarrer' && (
                <FeuilleDemarrer
                    chambre={chambre}
                    peutAffecter={peutAffecter}
                    onFermer={() => setFeuille(null)}
                    onDemarre={apres(t('menage.fiche.menageCommence'))}
                />
            )}
            {feuille === 'ndd' && (
                <FeuilleNePasDeranger
                    serviceId={chambre.service_id}
                    onFermer={() => setFeuille(null)}
                    onPose={apres(t('menage.fiche.nddPose'))}
                />
            )}
            {signalement.isReviewOpen && <FeuilleSignalement signalement={signalement} />}
        </Screen>
    );
}
